import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, X, AlertTriangle } from "lucide-react";
import type { LoadWorkflowResponse } from "../providers/supabase/dataProvider";

const CHECK_LABELS: Record<string, string> = {
  vendor_terms: "Vendor Terms are Present",
  customer_terms: "Customer Terms are Present",
  freight_terms: "Freight Terms are Present",
  margin: "Margin is Positive",
  gross_profit: "Gross Profit (USD)",
  cashflow: "Cashflow",
  payment_lag: "Payment Lag",
  gp_impact: "Cost of Cash Impact on GP",
  sell_material_currency: "Sell Material Currency Match",
  buy_material_currency: "Buy Material Currency Match",
  freight_terms_freight: "Freight Charge",
  origin_city: "Origin City",
  destination_city: "Destination City",
  resale_certificate: "Valid Resale Certificate",
  sales_order_document: "Sales Order Document Exists",
  signed_so_document: "Signed Sales Order Document",
  purchase_order_document: "Purchase Order Document Exists",
  booking_confirmation: "Booking Confirmation",
  bolt_seal_required: "Bolt Seal Required",
  invoice: "Invoice Exists",
  ready_to_invoice: "Ready to Invoice",
  material_actual_weight: "Actual Weight",
  seal_number: "Seal Number",
  container_number: "Container Number",
  no_requirements: "No Requirements",
  authorization: "Authorization",
  status: "Status",
  action: "Action",
  reason: "Reason",
};

export const LoadWorkflowModal = ({
  open,
  onOpenChange,
  title,
  response,
  requiresReason,
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  response: LoadWorkflowResponse | null;
  requiresReason: boolean;
  onConfirm: (reason?: string) => void;
  loading: boolean;
}) => {
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  if (!response) return null;

  const checks = Object.entries(response.checks);
  const warnings = checks.filter(([, check]) => check.warn);
  const hasWarnings = warnings.length > 0;
  const canExecute = response.valid && (!hasWarnings || acknowledged);

  const handleConfirm = () => {
    onConfirm(requiresReason ? reason : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Workflow Validation: {title}</DialogTitle>
          <DialogDescription>
            Review the validation checks before confirming this workflow
            transition.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-medium">Check</th>
                <th className="text-left py-2 font-medium">Detail</th>
                <th className="text-right py-2 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {checks.length === 0 ? (
                <tr>
                  <td className="py-2">No additional requirements</td>
                  <td className="py-2">OK</td>
                  <td className="py-2 text-right">
                    <Check className="inline h-4 w-4 text-green-600" />
                  </td>
                </tr>
              ) : (
                checks.map(([key, check]) => (
                  <tr key={key} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">
                      {CHECK_LABELS[key] ?? key}
                    </td>
                    <td className="py-2 pr-4">{check.detail}</td>
                    <td className="py-2 text-right">
                      {check.warn ? (
                        <AlertTriangle className="inline h-4 w-4 text-amber-500" />
                      ) : check.result ? (
                        <Check className="inline h-4 w-4 text-green-600" />
                      ) : (
                        <X className="inline h-4 w-4 text-red-600" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {hasWarnings && (
          <div className="flex items-center gap-2 mt-4">
            <input
              id="acknowledge-warnings"
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="acknowledge-warnings">Acknowledge Warnings</Label>
          </div>
        )}

        {requiresReason && (
          <div className="grid gap-2 mt-4">
            <Label htmlFor="workflow-reason">Reason</Label>
            <Input
              id="workflow-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason..."
            />
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Close
          </Button>
          {response.valid && (
            <Button
              onClick={handleConfirm}
              disabled={
                loading ||
                !canExecute ||
                (requiresReason && !reason.trim())
              }
            >
              Confirm
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
